import type { InsightsAssistantKind, InsightsChatItem, InsightsToolActivity } from "../types";
import { humanizeToolName } from "./humanize-tool";
import { summarizeToolActivity } from "./tool-activity-summary";

export type CoalescedToolActivity = {
  id: string;
  tool: string;
  pending: boolean;
  error: boolean;
  /** Prior attempt failed; a later same-tool call succeeded. */
  retried?: boolean;
  softRefuse?: boolean;
  softRefuseHint?: string;
  args?: unknown;
  result?: unknown;
  taskId?: string;
  /** Host wall-clock duration for this tool call (ms). */
  durationMs?: number;
  /** Short arg/result context for the row label. */
  summary?: string | null;
};

export type InsightsTranscriptRow =
  | { type: "user"; id: string; text: string; investigationId?: string }
  | {
      type: "assistant";
      id: string;
      text: string;
      streaming?: boolean;
      assistantKind?: InsightsAssistantKind;
      partId?: string;
    }
  | {
      type: "tool-stack";
      id: string;
      activities: CoalescedToolActivity[];
      /** True when this is the trailing tool group during an active run. */
      live: boolean;
    };

function resultStatus(result: unknown): string | undefined {
  if (!result || typeof result !== "object") return undefined;
  const status = (result as { status?: unknown }).status;
  return typeof status === "string" ? status : undefined;
}

function resultHint(result: unknown): string | undefined {
  if (!result || typeof result !== "object") return undefined;
  const hint = (result as { hint?: unknown }).hint;
  return typeof hint === "string" ? hint : undefined;
}

/** Merge adjacent call → result for the same tool into one logical activity. */
export function coalesceToolActivities(
  activities: InsightsToolActivity[],
  live: boolean,
): CoalescedToolActivity[] {
  const out: CoalescedToolActivity[] = [];
  for (let i = 0; i < activities.length; i += 1) {
    const current = activities[i]!;
    const next = activities[i + 1];
    if (current.phase === "call" && next?.phase === "result" && next.tool === current.tool) {
      const status = resultStatus(next.result);
      const softRefuse = Boolean(next.softRefuse) || status === "refused";
      const taskId = current.taskId ?? next.taskId;
      const summary = summarizeToolActivity(current.tool, current.args, next.result);
      const durationMs =
        typeof next.durationMs === "number"
          ? next.durationMs
          : typeof current.durationMs === "number"
            ? current.durationMs
            : undefined;
      out.push({
        id: current.id,
        tool: current.tool,
        pending: false,
        error: Boolean(next.error) && !softRefuse,
        ...(softRefuse ? { softRefuse: true, softRefuseHint: resultHint(next.result) } : {}),
        ...(taskId ? { taskId } : {}),
        ...(durationMs != null ? { durationMs } : {}),
        args: current.args,
        result: next.result,
        ...(summary ? { summary } : {}),
      });
      i += 1;
      continue;
    }
    if (current.phase === "call") {
      const summary = summarizeToolActivity(current.tool, current.args);
      out.push({
        id: current.id,
        tool: current.tool,
        pending: live,
        error: false,
        args: current.args,
        ...(current.taskId ? { taskId: current.taskId } : {}),
        ...(summary ? { summary } : {}),
      });
      continue;
    }
    const status = resultStatus(current.result);
    const softRefuse = Boolean(current.softRefuse) || status === "refused";
    const summary = summarizeToolActivity(current.tool, current.args, current.result);
    out.push({
      id: current.id,
      tool: current.tool,
      pending: false,
      error: Boolean(current.error) && !softRefuse,
      ...(softRefuse ? { softRefuse: true, softRefuseHint: resultHint(current.result) } : {}),
      ...(current.taskId ? { taskId: current.taskId } : {}),
      ...(typeof current.durationMs === "number" ? { durationMs: current.durationMs } : {}),
      result: current.result,
      ...(summary ? { summary } : {}),
    });
  }

  // Mark earlier failed attempts as retried when a later same-tool success exists.
  const lastSuccessByTool = new Map<string, number>();
  out.forEach((a, index) => {
    if (!a.pending && !a.error && !a.softRefuse) {
      lastSuccessByTool.set(a.tool, index);
    }
  });
  return out.map((a, index) => {
    const successAt = lastSuccessByTool.get(a.tool);
    if (a.error && successAt != null && successAt > index) {
      return { ...a, retried: true, error: false };
    }
    return a;
  });
}

/**
 * Collapse consecutive tool items (no intervening user/assistant) into tool-stacks.
 * Split whenever taskId changes so untagged pre-task tools stay out of later tasks
 * (provisional "Start Investigation" vs stamped census/gather panels).
 */
export function groupInsightsTranscript(
  items: InsightsChatItem[],
  running: boolean,
): InsightsTranscriptRow[] {
  const rows: InsightsTranscriptRow[] = [];
  let i = 0;

  while (i < items.length) {
    const item = items[i]!;
    if (item.kind === "user") {
      rows.push({
        type: "user",
        id: item.id,
        text: item.text,
        ...(item.investigationId ? { investigationId: item.investigationId } : {}),
      });
      i += 1;
      continue;
    }
    if (item.kind === "assistant") {
      rows.push({
        type: "assistant",
        id: item.id,
        text: item.text,
        streaming: item.streaming,
        ...(item.assistantKind ? { assistantKind: item.assistantKind } : {}),
        ...(item.partId ? { partId: item.partId } : {}),
      });
      i += 1;
      continue;
    }

    const raw: InsightsToolActivity[] = [];
    const startId = item.id;
    while (i < items.length) {
      const next = items[i]!;
      if (next.kind !== "tool") break;
      raw.push(next.activity);
      i += 1;
    }
    const isTrailing = i >= items.length;
    const live = running && isTrailing;
    const chunks = splitActivitiesByTaskId(raw);
    chunks.forEach((chunk, chunkIndex) => {
      const chunkLive = live && chunkIndex === chunks.length - 1;
      rows.push({
        type: "tool-stack",
        id: chunkIndex === 0 ? `stack-${startId}` : `stack-${startId}-${chunkIndex}`,
        activities: coalesceToolActivities(chunk, chunkLive),
        live: chunkLive,
      });
    });
  }

  return rows;
}

/** Homogeneous taskId runs (undefined/empty = provisional). */
function splitActivitiesByTaskId(activities: InsightsToolActivity[]): InsightsToolActivity[][] {
  if (!activities.length) return [];
  const chunks: InsightsToolActivity[][] = [];
  let current: InsightsToolActivity[] = [];
  let currentKey: string | null = null;

  for (const activity of activities) {
    const key = activity.taskId?.trim() || "";
    if (currentKey === null) {
      currentKey = key;
      current = [activity];
      continue;
    }
    if (key === currentKey) {
      current.push(activity);
      continue;
    }
    chunks.push(current);
    currentKey = key;
    current = [activity];
  }
  if (current.length) chunks.push(current);
  return chunks;
}

export function activeToolLabel(activities: CoalescedToolActivity[]): string {
  const pending = [...activities].reverse().find((a) => a.pending);
  const featured = pending ?? activities.at(-1);
  if (!featured) return "Working…";
  const name = humanizeToolName(featured.tool);
  const summary =
    featured.summary ?? summarizeToolActivity(featured.tool, featured.args, featured.result);
  return summary ? `${name} · ${summary}` : name;
}

/** Row label: human tool name + optional arg summary. */
export function toolActivityRowLabel(activity: CoalescedToolActivity): string {
  const name = humanizeToolName(activity.tool);
  const summary =
    activity.summary ?? summarizeToolActivity(activity.tool, activity.args, activity.result);
  return summary ? `${name} · ${summary}` : name;
}
